import React, { useState } from 'react';
import { useBrand } from '@/context/BrandContext';
import { EditableText } from '../ui/EditableText';
import { Upload } from 'lucide-react';
import { ImageUploadModal } from '../image-upload';
import { BrandSectionLayout } from './BrandSectionLayout';

interface DoDontSectionProps {
  section: any;
  isEditing: boolean;
  isGlobalDarkMode: boolean;
  editingElement: string | null;
  setEditingElement: (element: string | null) => void;
  updateSectionContent: (key: string, value: string) => void;
  updateSectionStyles: (key: string, styles: any) => void;
  pageNumber?: number;
}

export const DoDontSection: React.FC<DoDontSectionProps> = ({
  section,
  isEditing,
  isGlobalDarkMode,
  editingElement,
  setEditingElement,
  updateSectionContent,
  updateSectionStyles,
  pageNumber = 1
}) => {
  const { state } = useBrand();
  const [showImageModal, setShowImageModal] = useState(false);
  const [activeImageType, setActiveImageType] = useState<'usage1' | 'usage2' | 'usage3' | 'usage4' | 'usage5' | 'usage6'>('usage1');

  const handleImageSelect = (imageUrl: string) => {
    updateSectionContent(activeImageType, imageUrl);
    setShowImageModal(false);
  };

  const openImageModal = (imageType: 'usage1' | 'usage2' | 'usage3' | 'usage4' | 'usage5' | 'usage6') => {
    setActiveImageType(imageType);
    setShowImageModal(true);
  };

  return (
    <BrandSectionLayout
      section={section}
      isEditing={isEditing}
      isGlobalDarkMode={isGlobalDarkMode}
      editingElement={editingElement}
      setEditingElement={setEditingElement}
      updateSectionContent={updateSectionContent}
      updateSectionStyles={updateSectionStyles}
      pageNumber={pageNumber}
    >
      {/* Two-column layout */}
      <div className="h-full flex gap-16">
        {/* Left Column - 25% width */}
        <div className="flex flex-col h-full" style={{ width: '25%' }}>
          {/* Logo Usage Title */}
          <div className="flex flex-col items-start mb-6">
            <EditableText
              value={section.content.mainTitle || 'Logo Usage'}
              onChange={(value) => updateSectionContent('mainTitle', value)}
              element="h3"
              className="text-3xl leading-tight tracking-tight font-bold mb-6"
              placeholder="Logo Usage"
              multiline={true}
              editModeEnabled={isEditing}
              isEditing={isEditing && editingElement === 'mainTitle'}
              onEditStart={() => setEditingElement('mainTitle')}
              onEditEnd={() => setEditingElement(null)}
              onStyleChange={(styles) => updateSectionStyles('mainTitle', styles)}
              initialStyles={{
                fontFamily: state.brandData?.globalTitleFont || 'Inter',
                fontWeight: 'bold',
                ...section.content.mainTitleStyles
              }}
              isGlobalDarkMode={isGlobalDarkMode}
              sectionBackgroundColor={section.customization?.backgroundColor || section.styles.backgroundColor}
            />
          </div>

          {/* Logo Usage Description */}
          <div className="space-y-4">
            <EditableText
              value={section.content.usageDescription || 'These examples demonstrate common mistakes to avoid when using the logo. Following these guidelines ensures consistent brand representation.'}
              onChange={(value) => updateSectionContent('usageDescription', value)}
              element="p"
              className="text-base leading-relaxed"
              placeholder="Logo Usage Description"
              multiline={true}
              editModeEnabled={isEditing}
              isEditing={isEditing && editingElement === 'usageDescription'}
              onEditStart={() => setEditingElement('usageDescription')}
              onEditEnd={() => setEditingElement(null)}
              onStyleChange={(styles) => updateSectionStyles('usageDescription', styles)}
              initialStyles={{
                fontFamily: state.brandData?.globalTextFont || 'Inter',
                ...section.content.usageDescriptionStyles
              }}
              isGlobalDarkMode={isGlobalDarkMode}
              sectionBackgroundColor={section.customization?.backgroundColor || section.styles.backgroundColor}
            />
          </div>
        </div>

        {/* Right Column - 75% width, 3x2 image grid */}
        <div className="h-full w-full" style={{ width: '75%' }}>
          {/* 3x2 Grid Container */}
          <div className="grid grid-cols-3 grid-rows-2 gap-4 h-full w-full">
            {/* All 6 Usage Images */}
            {['usage1', 'usage2', 'usage3', 'usage4', 'usage5', 'usage6'].map((imageKey, index) => (
              <div key={imageKey} className="relative group h-full w-full flex flex-col">
                {/* Best Practice Label */}
                <div className="mb-2">
                  <EditableText
                    value={section.content[`${imageKey}Label`] || `Best Practice ${index + 1}`}
                    onChange={(value) => updateSectionContent(`${imageKey}Label`, value)}
                    element="span"
                    className="text-sm font-semibold"
                    placeholder={`Best Practice ${index + 1}`}
                    editModeEnabled={isEditing}
                    isEditing={isEditing && editingElement === `${imageKey}Label`}
                    onEditStart={() => setEditingElement(`${imageKey}Label`)}
                    onEditEnd={() => setEditingElement(null)}
                    onStyleChange={(styles) => updateSectionStyles(`${imageKey}Label`, styles)}
                    initialStyles={{
                      fontFamily: state.brandData?.globalTextFont || 'Inter',
                      ...section.content[`${imageKey}LabelStyles`]
                    }}
                    isGlobalDarkMode={isGlobalDarkMode}
                    sectionBackgroundColor={section.customization?.backgroundColor || section.styles.backgroundColor}
                  />
                </div>

                {/* Image Container without border */}
                <div className="flex-1 w-full relative rounded-md overflow-hidden">
                  <img
                    src={section.content[imageKey] || '/img-placeholder-16-9.png'}
                    alt={`Logo Usage ${index + 1}`}
                    className="w-full h-full object-contain"
                    onError={(e) => {
                      e.currentTarget.src = '/img-placeholder-16-9.png';
                    }}
                  />

                  {/* Upload Overlay - Only visible in edit mode */}
                  {isEditing && (
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center">
                      <div className="text-center text-white">
                        <Upload className="w-4 h-4 mx-auto mb-1" />
                        <p className="text-xs font-medium mb-1">Change Image #{index + 1}</p>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openImageModal(imageKey as any);
                          }}
                          className="px-2 py-1 bg-white/90 text-black rounded text-xs hover:bg-white transition-colors"
                        >
                          Upload
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Image Upload Modal */}
      <ImageUploadModal
        isOpen={showImageModal}
        onClose={() => setShowImageModal(false)}
        onImageSelect={handleImageSelect}
        isGlobalDarkMode={isGlobalDarkMode}
        title="Upload Logo Usage Image"
        acceptedFormats={['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg']}
        maxFileSize={10}
        showUnsplash={true}
        initialSearchQuery="logo usage brand guidelines"
      />
    </BrandSectionLayout>
  );
};